import type {
  Availability,
  GridMatch,
  GridSlot,
  GridTeam,
  SharedGrid,
} from '@/types/tournament.model';
import type { TournamentRound } from '@/types/tournament-round.model';
import { formatPlayerName } from '@/utils/team-utils';
import { minutesToTime, timeToMinutes } from '@/utils/time';
import { addDays } from '@/utils/localDateOnly';

/**
 * A stable color per tournament. The index comes from the order of `grid.tournaments`.
 * Lives here (a plain `.ts` file) rather than in `GridMatchCard.tsx` so it doesn't trip
 * `react-refresh/only-export-components` — that rule only allows literal constant exports
 * next to a component, and an array doesn't qualify.
 */
export const TOURNAMENT_DOT_COLORS = [
  'bg-sky-500',
  'bg-violet-500',
  'bg-amber-500',
  'bg-emerald-500',
  'bg-rose-500',
  'bg-cyan-500',
];

/**
 * What a card needs to know about the tournament a match belongs to. Resolved once per grid so
 * the name and the color can't be looked up by two different mechanisms and drift apart.
 */
export type TournamentBadge = { name: string; colorIndex: number };

/**
 * The week/day toggle's mode. Lives here, not in `GridViewSwitch.tsx`, because it's shared
 * between that component and `SharedGridView.tsx` — `grid-model.ts` is where the grid's shared
 * types live. (Unlike `TOURNAMENT_DOT_COLORS` above, a type-only export never trips
 * `react-refresh/only-export-components`: the rule skips `ExportNamedDeclaration` nodes with
 * `exportKind === "type"` before it even checks what's exported.)
 */
export type GridViewMode = 'week' | 'day';

/**
 * `tournamentId` -> its position in `grid.tournaments`.
 *
 * Shared so the screen and the PDF can never disagree on it: it feeds `sortForCourts`, which is
 * what decides court numbers, and a second hand-rolled copy of this rule would silently
 * desynchronise the numbers on paper from the ones on screen. The explicit generics matter —
 * `new Map(xs.map(x => [a, b]))` infers the inner arrays as `(string | number)[]`, not as
 * tuples, and the Map constructor then rejects them.
 */
export function tournamentOrderOf(
  tournaments: { id: string }[],
): Map<string, number> {
  return new Map<string, number>(tournaments.map((t, i) => [t.id, i]));
}

/** A grid slot: `date` in `YYYY-MM-DD`, `time` in `HH:mm`. */
export type GridSlotRef = { date: string; time: string };

/** Cell key. Date and time are enough: the grid is already filtered by club. */
export function cellKey(date: string, startTime: string): string {
  return `${date}T${startTime}`;
}

/**
 * Splits a local `YYYY-MM-DDTHH:mm[:ss]` string into the two halves the grid compares against
 * slots. Parsed as text rather than with `new Date`, which would reinterpret it in the browser's
 * timezone — the backend already converted it to the match's own.
 */
function splitLocalDateTime(value: string): { date: string; time: string } {
  const [date, rest = ''] = value.split('T');
  return { date, time: rest.slice(0, 5) };
}

/**
 * The pending-move wire format: local time, `YYYY-MM-DDTHH:mm:ss`, which is what the backend
 * expects. It lives next to `splitLocalDateTime` so the write and read sides of this string
 * can't drift apart — nothing else in the type system ties them together.
 */
export function toPendingMove(date: string, time: string): string {
  return `${date}T${time}:00`;
}

/** The match's local date and time, as strings comparable with the slots'. */
export function localDateTimeOf(match: GridMatch): {
  date: string;
  time: string;
} {
  return splitLocalDateTime(match.startDate);
}

/** Where the match currently sits, counting moves that haven't been saved yet. */
export function effectiveCell(
  match: GridMatch,
  moves: Map<string, string>,
): { date: string; time: string } {
  const pending = moves.get(match.matchId);
  return pending ? splitLocalDateTime(pending) : localDateTimeOf(match);
}

export type CellModel = {
  key: string;
  date: string;
  startTime: string;
  /** Null when no block of the active club declares this cell. */
  courtsQuantity: number | null;
  /** Whether a block of the active club actually declares this (date, time). */
  declared: boolean;
  matches: GridMatch[];
  /** More matches than declared courts. Never true when capacity is unknown. */
  overbooked: boolean;
};

export type GridModel = {
  dates: string[];
  times: string[];
  cells: Map<string, CellModel>;
  /** Times on the axis that `hiddenTimes` took out of the view, sorted. */
  hiddenTimes: string[];
};

/**
 * The backend returns matches from every club in the grid, not just the active one, and
 * `clubId` can be null (no club resolved for the match). A match clearly parked at a
 * *different declared* club doesn't belong in this view — showing it here would count it
 * against courts that aren't its own, the exact miscount the club selector exists to prevent.
 *
 * Matches with a null clubId, or with a club that isn't in `grid.clubs`, are deliberately kept:
 * they have no other view to appear in, and hiding them would make them invisible everywhere.
 */
function belongsToAnotherClub(
  match: GridMatch,
  clubId: string,
  grid: SharedGrid,
): boolean {
  return (
    match.clubId !== null &&
    match.clubId !== clubId &&
    grid.clubs.some((c) => c.id === match.clubId)
  );
}

/**
 * Builds the matrix for one club: columns by date, rows by start time, and matches distributed
 * into cells based on where they currently sit (counting pending moves).
 *
 * Every (date, time) intersection gets a cell, not just the ones a block declares. The axes are
 * the union of what the blocks declare, what the matches actually occupy, and what the organizer
 * added by hand — so a match parked at an arbitrary time (21:30 against a 21-to-22 block) shows
 * up *where it is* instead of in a footnote, and an undeclared cell is still a valid drop target.
 * Nothing in the backend requires a declared slot: `moverPartidosEnGrilla` never checks it.
 *
 * `hiddenTimes` are rows the organizer took out of the view. They only hide a row nothing sits on:
 * a time that holds a match stays on the axis whatever this says, so hiding can never make a
 * match disappear (say, one that a refetch or a pending move puts back on that time).
 */
export function buildGrid(
  grid: SharedGrid,
  clubId: string,
  moves: Map<string, string>,
  extraTimes: ReadonlySet<string> = new Set(),
  hiddenTimes: ReadonlySet<string> = new Set(),
): GridModel {
  const declaredByKey = new Map<string, number | null>();
  const declaredDates = new Set<string>();
  const declaredTimes = new Set<string>();

  grid.slots
    .filter((s) => s.clubId === clubId)
    .forEach((slot: GridSlot) => {
      const key = cellKey(slot.date, slot.startTime);
      declaredDates.add(slot.date);
      declaredTimes.add(slot.startTime);
      // Two blocks of the same club can declare the same slot: capacity is the max, not the
      // sum — they're the same physical courts. The backend already aggregates this; this is
      // just in case.
      declaredByKey.set(
        key,
        declaredByKey.has(key)
          ? maxCourts(declaredByKey.get(key) ?? null, slot.courtsQuantity)
          : slot.courtsQuantity,
      );
    });

  const placed = grid.matches
    .filter((match) => !belongsToAnotherClub(match, clubId, grid))
    .map((match) => ({ match, at: effectiveCell(match, moves) }));

  const dates = [
    ...new Set([...declaredDates, ...placed.map((p) => p.at.date)]),
  ].sort();
  const occupiedTimes = new Set(placed.map((p) => p.at.time));
  const axisTimes = [...new Set([...declaredTimes, ...occupiedTimes, ...extraTimes])].sort();
  const isHidden = (time: string) => !occupiedTimes.has(time) && hiddenTimes.has(time);
  const times = axisTimes.filter((time) => !isHidden(time));

  const cells = new Map<string, CellModel>();
  dates.forEach((date) => {
    times.forEach((startTime) => {
      const key = cellKey(date, startTime);
      cells.set(key, {
        key,
        date,
        startTime,
        // `has` and not `?? null`: a legitimately declared old block can carry a null
        // capacity, which is not the same thing as "no block declares this cell".
        courtsQuantity: declaredByKey.get(key) ?? null,
        declared: declaredByKey.has(key),
        matches: [],
        overbooked: false,
      });
    });
  });

  placed.forEach(({ match, at }) => {
    // Safe by construction: every (date, time) a match sits at fed the axes above.
    cells.get(cellKey(at.date, at.time))?.matches.push(match);
  });

  cells.forEach((cell) => {
    cell.overbooked =
      cell.courtsQuantity !== null && cell.matches.length > cell.courtsQuantity;
  });

  return { dates, times, cells, hiddenTimes: axisTimes.filter(isHidden) };
}

function maxCourts(a: number | null, b: number | null): number | null {
  if (a === null) return b;
  if (b === null) return a;
  return Math.max(a, b);
}

/**
 * How a pair is named across the grid.
 *
 * Bracket placeholders have no players yet. When the knockout tree tells us which qualifier fills
 * the slot ("2° Grupo B"), that is what the organizer wants to read; `fallback` is the last resort
 * for the rounds that carry no such information — semifinals and the final, whose slots say
 * "winner of an earlier match" and have no label in the backend either.
 */
export function teamLabel(
  team: GridTeam,
  fallback: string,
  placeholder?: string,
): string {
  if (team.players.length === 0) return placeholder ?? fallback;
  return team.players.map((p) => formatPlayerName(p.name)).join(' / ');
}

/**
 * Labels for a match's two still-undefined slots: a bracket qualifier (`"2° Grupo B"`) or, in a
 * group of 4's dependent match, its source match (`"Ganador Partido 1"`).
 */
export type PlaceholderLabels = { team1?: string; team2?: string };

/** matchId -> its slot labels. Empty when nothing in the grid is still a placeholder. */
export type PlaceholderLabelMap = Map<string, PlaceholderLabels>;

/**
 * The tournaments that still have a match with an unresolved team, split by where the labels for
 * it come from: a group match (a group of 4's dependent match) or a bracket match. Lets the page
 * fetch only the fixtures that can actually label something — usually none, once the group stage
 * finishes. `stage` is `"Grupo X"` for every group match; the backend builds it that way (in
 * `FixtureMatches.all`), and the grid payload carries no other field that tells the phase apart.
 */
export function tournamentsWithUnresolvedTeams(grid: SharedGrid): {
  groups: Set<string>;
  knockout: Set<string>;
} {
  const groups = new Set<string>();
  const knockout = new Set<string>();
  grid.matches
    .filter((m) => m.team1.players.length === 0 || m.team2.players.length === 0)
    .forEach((m) => {
      (m.stage.startsWith('Grupo') ? groups : knockout).add(m.tournamentId);
    });
  return { groups, knockout };
}

/**
 * `"09:30"` -> `"10:30"`. Caps at `"23:59"` instead of rolling into invalid `"24:xx"`: `GridSlot`
 * has no real end time, and this only feeds a slot's approximate `endTime` below, which just
 * needs to mark the slot's own start as "within range" for `fitsTournamentSchedule`.
 */
const END_OF_DAY_MINUTES = 23 * 60 + 59;

export function addOneHour(hhmm: string): string {
  const minutes = timeToMinutes(hhmm);
  if (minutes < 0) return hhmm;
  return minutesToTime(Math.min(minutes + 60, END_OF_DAY_MINUTES));
}

/**
 * Turns the active club's declared slots into the `Availability[]` shape the fine-grained edit
 * modal needs for its "is this time within the tournament's schedule" check.
 *
 * `dayOfWeek` is cast from an empty string: `fitsTournamentSchedule` only filters by `date`, so
 * it's genuinely unused here, and `GridSlot` carries no such field to derive it from.
 */
export function scheduleAvailabilityFor(
  grid: SharedGrid,
  clubId: string,
): Availability[] {
  const byDate = new Map<string, Availability>();
  grid.slots
    .filter((s) => s.clubId === clubId)
    .forEach((s) => {
      const entry = byDate.get(s.date) ?? {
        date: s.date,
        dayOfWeek: '' as Availability['dayOfWeek'],
        timeRange: [],
      };
      entry.timeRange.push({
        qty: 1,
        courtsQuantity: s.courtsQuantity ?? 0,
        startTime: s.startTime,
        endTime: addOneHour(s.startTime),
      });
      byDate.set(s.date, entry);
    });
  return [...byDate.values()];
}

/** A week of a LONG tournament, shown on its own in the grid. */
export type GridWeek = { start: string; end: string; label: string };

function shortDate(ymd: string): string {
  return new Date(ymd + 'T00:00:00').toLocaleDateString('es-AR', {
    day: '2-digit',
    month: '2-digit',
  });
}

/**
 * One entry per week the rounds cover, in order. Rounds that share a week (a group round and a knockout
 * round can fall on the same one) share the entry and appear together in its label.
 */
export function weeksFromRounds(rounds: TournamentRound[]): GridWeek[] {
  const byStart = new Map<string, { end: string; numbers: number[] }>();
  for (const round of rounds) {
    const entry = byStart.get(round.weekStartDate) ?? {
      end: round.weekEndDate ?? addDays(round.weekStartDate, 6),
      numbers: [],
    };
    entry.numbers.push(round.roundNumber);
    byStart.set(round.weekStartDate, entry);
  }
  return [...byStart.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([start, { end, numbers }]) => ({
      start,
      end,
      label: `Fecha ${[...numbers].sort((a, b) => a - b).join(' y ')} · ${shortDate(start)} al ${shortDate(end)}`,
    }));
}

/** The week the grid opens on: the round in progress, else the last published one, else the first. */
export function initialWeekStart(rounds: TournamentRound[]): string | null {
  const inProgress = rounds.find(
    (r) => r.status === 'AVAILABILITY_OPEN' || r.status === 'PROPOSED',
  );
  const lastPublished = [...rounds].reverse().find((r) => r.status === 'PUBLISHED');
  return (inProgress ?? lastPublished ?? rounds[0])?.weekStartDate ?? null;
}

/** The slots and matches that fall in one week; the rest of the grid is left as is. */
export function gridForWeek(grid: SharedGrid, week: GridWeek): SharedGrid {
  const inWeek = (date: string) => date >= week.start && date <= week.end;
  return {
    ...grid,
    slots: grid.slots.filter((s) => inWeek(s.date)),
    matches: grid.matches.filter((m) => inWeek(localDateTimeOf(m).date)),
  };
}
